import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface GoogleConnection {
  id: string;
  user_id: string;
  connected_at: string;
  updated_at: string;
}

export function useGoogleConnection() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const {
    data: connection,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["google-connection", user?.id],
    queryFn: async (): Promise<GoogleConnection | null> => {
      if (!user?.id) return null;

      // Use security definer function scoped to auth.uid()
      const { data, error } = await supabase
        .rpc("get_my_google_connection")
        .maybeSingle();

      if (error) throw error;
      return (data as unknown as GoogleConnection) ?? null;
    },
    enabled: !!user?.id,
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("google_connections")
        .delete()
        .eq("user_id", user.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["google-connection"] });
    },
  });

  return {
    isConnected: !!connection,
    connection,
    isLoading,
    error,
    refetch,
    disconnect: disconnectMutation.mutate,
    isDisconnecting: disconnectMutation.isPending,
  };
}
